import { useEffect, useState } from "react";
import { CalendarClock, Check, Edit, IndianRupee, Loader2, Trash, TrendingUp } from "lucide-react";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";

import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { ConfirmPopupModal, DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { checkUserPermissionFunc, truncateText } from "../../components/common/CommonFunctions";
import { DA_REVISION, DA_REVISION_APPLY, DA_REVISION_PREVIEW, EMPLOYEE_DEPARTMENT, EMPLOYEE_DESIGNATION, EMPLOYEE_GROUP } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../components/ui/fieldStyles";

// Mirrors configuration/constants.py. Percentage of Basic is the pay-commission
// standard; "gross" exists because that is what the payroll engine did before
// revisions were introduced, so an older period can still be expressed.
const RATE_BASE = [
    { value: "basic", label: "% of Basic" },
    { value: "basic_da", label: "% of Basic + DA" },
    { value: "gross", label: "% of Gross" },
];

const RATE_TYPE = [
    { value: "percent", label: "Percentage" },
    { value: "flat", label: "Flat amount" },
];

// Additive raises gross and CTC by the DA delta — a real revision. Rebalance
// holds gross and absorbs the change in the other-allowance component.
const CTC_MODE = [
    { value: "additive", label: "Increase gross & CTC" },
    { value: "rebalance", label: "Rebalance within gross" },
];

const SCOPE_TYPE = [
    { value: "company", label: "Whole company" },
    { value: "employee_group", label: "Employee group" },
    { value: "designation", label: "Designation" },
    { value: "department", label: "Department" },
];

const STATUS_CHIP = {
    draft: "bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-300",
    approved: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    applied: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    superseded: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
};

const EMPTY_FORM = {
    id: null,
    revision_name: "",
    effective_from: null,
    announced_on: null,
    scope_type: "company",
    scope_ids: [],
    rate_type: "percent",
    rate_value: "",
    rate_base: "basic",
    ctc_mode: "additive",
    reference: "",
    notes: "",
};

const money = (value) =>
    Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DARevision = () => {
    const { fetch, create, update } = useApiMethods();

    const [revisions, setRevisions] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);
    // The register response carries the earliest still-open effective date in
    // `extras`, so the date picker can disable anything already settled instead
    // of letting the user save and then be refused.
    const [wholeResponse, setWholeResponse] = useState(null);

    const [openDialog, setOpenDialog] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [scopeOptions, setScopeOptions] = useState([]);
    const [scopeOptionsLoading, setScopeOptionsLoading] = useState(false);

    const [selectedRow, setSelectedRow] = useState(null);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [isApproveConfirmOpen, setIsApproveConfirmOpen] = useState(false);
    const [isApplyConfirmOpen, setIsApplyConfirmOpen] = useState(false);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [preview, setPreview] = useState(null);

    const { add: addPermission, edit: editPermission, draft: draftPermission } =
        checkUserPermissionFunc("configuration", "DA Revision", "SubMenu");

    const firstOpenDate = wholeResponse?.data?.extras?.first_open_effective_date || null;

    const resetForm = () => {
        setFormData(EMPTY_FORM);
        setEditMode(false);
        setOpenDialog(false);
        setSelectedRow(null);
    };

    const setField = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

    const scopeLabel = SCOPE_TYPE.find((item) => item.value === formData.scope_type)?.label || "selection";

    const loadScopeOptions = async (scopeType) => {
        if (!scopeType || scopeType === "company") {
            setScopeOptions([]);
            return;
        }
        const endpoint = {
            employee_group: EMPLOYEE_GROUP,
            designation: EMPLOYEE_DESIGNATION,
            department: EMPLOYEE_DEPARTMENT,
        }[scopeType];
        if (!endpoint) {
            setScopeOptions([]);
            return;
        }
        setScopeOptionsLoading(true);
        try {
            const response = await fetch(endpoint, { drop_down: "True" });
            const raw = Array.isArray(response?.data)
                ? response.data
                : (response?.data?.result || response?.data?.data || []);
            const options = (Array.isArray(raw) ? raw : []).map((item) => ({
                value: item.id,
                label:
                    item.employee_group_name ||
                    item.designation_name ||
                    item.department_name ||
                    item.name ||
                    `ID ${item.id}`,
            }));
            setScopeOptions(options);
        } catch (_) {
            setScopeOptions([]);
        } finally {
            setScopeOptionsLoading(false);
        }
    };

    useEffect(() => {
        if (openDialog) {
            loadScopeOptions(formData.scope_type);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openDialog, formData.scope_type]);

    // Anything on or before the last published payroll month is settled: changing
    // it would rewrite payslips employees already have, so it is refused on the
    // server too. Disabling it here just makes that visible earlier.
    const disableSettledDates = (current) => {
        if (!current || !firstOpenDate) return false;
        return current.isBefore(dayjs(firstOpenDate), "day");
    };

    const openForEdit = (row) => {
        setEditMode(true);
        setSelectedRow(row);
        setFormData({
            id: row.id,
            revision_name: row.revision_name || "",
            effective_from: row.effective_from ? dayjs(row.effective_from) : null,
            announced_on: row.announced_on ? dayjs(row.announced_on) : null,
            scope_type: row.scope_type || "company",
            scope_ids: row.scope_ids || [],
            rate_type: row.rate_type || "percent",
            rate_value: row.rate_value ?? "",
            rate_base: row.rate_base || "basic",
            ctc_mode: row.ctc_mode || "additive",
            reference: row.reference || "",
            notes: row.notes || "",
        });
        setOpenDialog(true);
    };

    const payload = (isDraft) => ({
        ...(formData.id ? { id: formData.id } : {}),
        revision_name: formData.revision_name,
        effective_from: formData.effective_from ? dayjs(formData.effective_from).format("YYYY-MM-DD") : null,
        announced_on: formData.announced_on ? dayjs(formData.announced_on).format("YYYY-MM-DD") : null,
        scope_type: formData.scope_type,
        scope_ids: formData.scope_type === "company" ? [] : formData.scope_ids,
        rate_type: formData.rate_type,
        rate_value: formData.rate_value === "" ? null : Number(formData.rate_value),
        rate_base: formData.rate_base,
        ctc_mode: formData.ctc_mode,
        reference: formData.reference,
        notes: formData.notes,
        is_draft: isDraft,
    });

    const save = async (isDraft) => {
        if (!formData.effective_from) {
            ToastMsg("error", "Please choose the effective from date");
            return;
        }
        if (!isDraft && formData.scope_type !== "company" && !(formData.scope_ids || []).length) {
            ToastMsg("error", `Please choose at least one ${scopeLabel.toLowerCase()} for this scope`);
            return;
        }
        if (!isDraft && (formData.rate_value === "" || formData.rate_value === null)) {
            ToastMsg("error", "Please enter the DA rate");
            return;
        }
        setLoading(true);
        const body = payload(isDraft);
        const response = editMode ? await update(DA_REVISION, body) : await create(DA_REVISION, body);
        setLoading(false);

        if (response?.status === "success") {
            ToastMsg("success", response.message || "DA revision saved successfully");
            resetForm();
            setServerTrigger((prev) => !prev);
        } else {
            // The server returns per-field messages under `errors`; showing the
            // first one is more useful than the generic summary.
            const fieldError = response?.errors && Object.values(response.errors)[0];
            ToastMsg("error", fieldError || response?.message || "Failed to save DA revision");
        }
    };

    const runAction = async (body, fallbackError) => {
        setLoading(true);
        const response = await update(DA_REVISION, body);
        setLoading(false);
        if (response?.status === "success") {
            ToastMsg("success", response.message);
            setServerTrigger((prev) => !prev);
            return true;
        }
        ToastMsg("error", response?.message || fallbackError);
        return false;
    };

    const handleDeleteDraft = async () => {
        if (!selectedRow) return;
        await runAction({ id: selectedRow.id, draft_delete: true }, "Failed to delete draft");
        setIsDeleteConfirmOpen(false);
        setSelectedRow(null);
    };

    const handleApprove = async () => {
        if (!selectedRow) return;
        await runAction({ id: selectedRow.id, approve: true }, "Failed to approve revision");
        setIsApproveConfirmOpen(false);
        setSelectedRow(null);
    };

    const handleApply = async () => {
        if (!selectedRow) return;
        setLoading(true);
        const response = await create(DA_REVISION_APPLY, { id: selectedRow.id });
        setLoading(false);
        if (response?.status === "success") {
            ToastMsg("success", response.message);
            setServerTrigger((prev) => !prev);
        } else {
            ToastMsg("error", response?.message || "Failed to apply revision");
        }
        setIsApplyConfirmOpen(false);
        setSelectedRow(null);
    };

    const openPreview = async (row) => {
        setSelectedRow(row);
        setPreview(null);
        setPreviewOpen(true);
        setPreviewLoading(true);
        const response = await fetch(DA_REVISION_PREVIEW, { id: row.id });
        setPreviewLoading(false);
        if (response?.status === "success") {
            setPreview(response.data);
        } else {
            ToastMsg("error", response?.message || "Failed to load the impact preview");
            setPreviewOpen(false);
        }
    };

    const columns = [
        { accessor: "code", Header: "Code", sortable: false, filterable: true },
        {
            accessor: "revision_name",
            Header: "Revision",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => truncateText(value, 24, "260px", "balance", "-"),
        },
        {
            accessor: "effective_from",
            Header: "In force",
            sortable: true,
            filterType: "date",
            // One column for the span, because "which rate applied when" is the
            // question this register exists to answer. effective_to is derived on
            // the server from the next revision in the same scope.
            Cell: ({ row }) => (
                <span className="whitespace-nowrap tabular-nums">
                    {row.effective_from ? dayjs(row.effective_from).format("DD-MM-YYYY") : "-"}
                    {" → "}
                    {row.effective_to ? dayjs(row.effective_to).format("DD-MM-YYYY") : "current"}
                </span>
            ),
        },
        {
            accessor: "rate_value",
            Header: "Rate",
            sortable: true,
            Cell: ({ row }) => (
                <span className="whitespace-nowrap tabular-nums font-medium">
                    {row.rate_type === "flat"
                        ? `₹ ${money(row.rate_value)}`
                        : `${Number(row.rate_value || 0)}% ${
                              RATE_BASE.find((b) => b.value === row.rate_base)?.label.replace("% of ", "of ") || ""
                          }`}
                </span>
            ),
        },
        {
            accessor: "scope_type",
            Header: "Applies to",
            filterable: true,
            filterType: "select",
            filterOptions: SCOPE_TYPE.map((item) => ({ value: item.value, label: item.label })),
            Cell: ({ value }) => SCOPE_TYPE.find((s) => s.value === value)?.label || value || "-",
        },
        {
            accessor: "status",
            Header: "Status",
            filterable: true,
            filterType: "select",
            filterOptions: [
                { value: "draft", label: "draft" },
                { value: "approved", label: "approved" },
                { value: "applied", label: "applied" },
                { value: "superseded", label: "superseded" },
            ],
            Cell: ({ value, row }) => (
                <span className="flex items-center gap-2">
                    <span
                        className={cn(
                            "px-2 py-1 text-xs font-medium rounded-full capitalize",
                            STATUS_CHIP[value] || STATUS_CHIP.draft,
                        )}
                    >
                        {value || "draft"}
                    </span>
                    {value === "applied" && row.applied_count ? (
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {row.applied_count} employees
                        </span>
                    ) : null}
                </span>
            ),
        },
        {
            accessor: "reference",
            Header: "Reference",
            Cell: ({ value }) => truncateText(value, 18, "220px", "balance", "-"),
        },
        {
            accessor: "action",
            Header: "Actions",
            sortable: false,
            Cell: ({ row }) => {
                const isSettled = row.status === "applied" || row.status === "superseded";
                return (
                    <div className="flex items-center gap-1">
                        {/* Preview is safe for anyone who can see the screen: it
                            writes nothing and is the only place the PF impact shows. */}
                        <Button
                            variant="ghost"
                            size="sm"
                            title="Preview impact"
                            className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                            onClick={() => openPreview(row)}
                        >
                            <TrendingUp className="h-4 w-4" />
                        </Button>

                        {editPermission && !isSettled && (
                            <Button
                                variant="ghost"
                                size="sm"
                                title="Edit"
                                className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                onClick={() => openForEdit(row)}
                            >
                                <Edit className="h-4 w-4" />
                            </Button>
                        )}

                        {editPermission && (row.status === "draft" || !row.status) && (
                            <Button
                                variant="ghost"
                                size="sm"
                                title="Approve"
                                className="text-sidebar-primary hover:bg-sidebar-accent dark:hover:bg-gray-700"
                                onClick={() => {
                                    setSelectedRow(row);
                                    setIsApproveConfirmOpen(true);
                                }}
                            >
                                <Check className="h-4 w-4" />
                            </Button>
                        )}

                        {editPermission && row.status === "approved" && (
                            <Button
                                variant="ghost"
                                size="sm"
                                title="Apply to employees"
                                className="text-emerald-600 hover:bg-emerald-50 dark:hover:bg-gray-700"
                                onClick={() => {
                                    setSelectedRow(row);
                                    setIsApplyConfirmOpen(true);
                                }}
                            >
                                <IndianRupee className="h-4 w-4" />
                            </Button>
                        )}

                        {row.is_draft && (
                            <Button
                                variant="ghost"
                                size="sm"
                                title="Delete draft"
                                className="text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                                onClick={() => {
                                    setSelectedRow(row);
                                    setIsDeleteConfirmOpen(true);
                                }}
                            >
                                <Trash className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                );
            },
        },
    ];

    const totals = preview?.totals;

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={CalendarClock}
                title="DA Revision"
                subtitle="Record dearness allowance revisions with their effective dates and keep every past rate"
                totalRecords={totalRecords}
                buttonEnable={!!addPermission}
                setOpenDialog={setOpenDialog}
                buttonName="Add DA Revision"
            />

            <Reactable
                pagename="da-revision"
                columns={columns}
                data={revisions}
                setData={setRevisions}
                setTotalCount={setTotalRecords}
                setWholeResponse={setWholeResponse}
                url={DA_REVISION}
                serverSide={true}
                serverTrigger={serverTrigger}
                rowSize="sm"
                isCSVRequired={true}
                isPDFRequired={true}
                isDraftRequired={!!draftPermission}
                isDateRequired={false}
            />

            {/* Add / edit */}
            <DialogModal
                open={openDialog}
                onClose={resetForm}
                onSave={() => save(false)}
                onDraft={draftPermission ? () => save(true) : undefined}
                title={editMode ? "Edit DA Revision" : "Add DA Revision"}
                closeLabel="Cancel"
                saveLabel={editMode ? "Update" : "Save"}
                draftLabel="Draft"
                editMode={editMode}
                loading={loading}
                width="620px"
            >
                <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label htmlFor="effective_from" className={modalFieldLabelClass}>
                                Effective from <span className="text-red-500">*</span>
                            </label>
                            <DatePicker
                                id="effective_from"
                                className="w-full"
                                format="DD-MM-YYYY"
                                value={formData.effective_from}
                                disabledDate={disableSettledDates}
                                onChange={(date) => setField("effective_from", date)}
                                disabled={loading}
                            />
                            {firstOpenDate && (
                                <p className="text-[11px] text-muted-foreground">
                                    Payroll is settled up to {dayjs(firstOpenDate).subtract(1, "day").format("DD-MM-YYYY")}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="announced_on" className={modalFieldLabelClass}>
                                Announced on
                            </label>
                            <DatePicker
                                id="announced_on"
                                className="w-full"
                                format="DD-MM-YYYY"
                                value={formData.announced_on}
                                onChange={(date) => setField("announced_on", date)}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="revision_name" className={modalFieldLabelClass}>
                            Revision name
                        </label>
                        <Input
                            id="revision_name"
                            value={formData.revision_name}
                            onChange={(e) => setField("revision_name", e.target.value)}
                            placeholder="Named from the effective month if left blank"
                            disabled={loading}
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>Rate type</label>
                            <Select
                                className="w-full"
                                value={formData.rate_type}
                                options={RATE_TYPE}
                                onChange={(value) => setField("rate_type", value)}
                                disabled={loading}
                            />
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="rate_value" className={modalFieldLabelClass}>
                                {formData.rate_type === "flat" ? "Amount per month" : "Percentage"}{" "}
                                <span className="text-red-500">*</span>
                            </label>
                            <Input
                                id="rate_value"
                                type="number"
                                min="0"
                                step="0.001"
                                value={formData.rate_value}
                                onChange={(e) => setField("rate_value", e.target.value)}
                                placeholder={formData.rate_type === "flat" ? "1500" : "46"}
                                disabled={loading}
                            />
                        </div>

                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>Calculated on</label>
                            <Select
                                className="w-full"
                                value={formData.rate_base}
                                options={RATE_BASE}
                                onChange={(value) => setField("rate_base", value)}
                                disabled={loading || formData.rate_type === "flat"}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>Applies to</label>
                            <Select
                                className="w-full"
                                value={formData.scope_type}
                                options={SCOPE_TYPE}
                                onChange={(value) => setFormData((prev) => ({ ...prev, scope_type: value, scope_ids: [] }))}
                                disabled={loading}
                            />
                        </div>

                        {formData.scope_type !== "company" && (
                            <div className="space-y-2">
                                <label className={modalFieldLabelClass}>
                                    Select {scopeLabel.toLowerCase()} <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    className="w-full"
                                    mode="multiple"
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder={`Choose ${scopeLabel.toLowerCase()}(s)`}
                                    value={formData.scope_ids}
                                    options={scopeOptions}
                                    loading={scopeOptionsLoading}
                                    onChange={(value) => setField("scope_ids", value)}
                                    disabled={loading}
                                />
                            </div>
                        )}

                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>Effect on CTC</label>
                            <Select
                                className="w-full"
                                value={formData.ctc_mode}
                                options={CTC_MODE}
                                onChange={(value) => setField("ctc_mode", value)}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="reference" className={modalFieldLabelClass}>
                            Reference
                        </label>
                        <Input
                            id="reference"
                            value={formData.reference}
                            onChange={(e) => setField("reference", e.target.value)}
                            placeholder="Circular / GO number the revision is authorised by"
                            disabled={loading}
                        />
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="notes" className={modalFieldLabelClass}>
                            Notes
                        </label>
                        <Textarea
                            id="notes"
                            rows={2}
                            maxLength={150}
                            showCharCount={true}
                            value={formData.notes}
                            onChange={(e) => setField("notes", e.target.value)}
                            disabled={loading}
                        />
                    </div>
                </form>
            </DialogModal>

            {/* Impact preview. The PF column is the point of this screen: PF is
                charged on Basic + DA, so take-home rises by less than gross. */}
            <DialogModal
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                title={`Impact - ${selectedRow?.revision_name || selectedRow?.code || "DA revision"}`}
                closeLabel="Close"
                showActions={true}
                hideSaveButton={true}
                width="960px"
            >
                {previewLoading ? (
                    <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Calculating impact
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {[
                                { label: "Employees", value: totals?.employees ?? 0, prefix: "" },
                                { label: "DA increase", value: totals?.da_change ?? 0, prefix: "₹ " },
                                { label: "PF increase", value: totals?.pf_change ?? 0, prefix: "₹ " },
                                { label: "Take-home increase", value: totals?.take_home_change ?? 0, prefix: "₹ " },
                            ].map((tile) => (
                                <div
                                    key={tile.label}
                                    className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-3"
                                >
                                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{tile.label}</p>
                                    <p className="text-lg font-semibold tabular-nums text-gray-900 dark:text-white">
                                        {tile.prefix}
                                        {tile.prefix ? money(tile.value) : tile.value}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {preview?.errors?.length ? (
                            <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3">
                                <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
                                    {preview.errors.length} employee(s) cannot be revised
                                </p>
                                <ul className="mt-1 space-y-0.5 text-xs text-amber-800/90 dark:text-amber-300/90">
                                    {preview.errors.slice(0, 5).map((row) => (
                                        <li key={row.employee_id}>
                                            {row.employee_code || row.employee_id}: {row.error}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}

                        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                            <table className="min-w-full text-sm">
                                <thead className="bg-gray-50 dark:bg-gray-800">
                                    <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                                        <th className="px-3 py-2 font-medium">Employee</th>
                                        <th className="px-3 py-2 font-medium text-right">Basic</th>
                                        <th className="px-3 py-2 font-medium text-right">DA now</th>
                                        <th className="px-3 py-2 font-medium text-right">DA revised</th>
                                        <th className="px-3 py-2 font-medium text-right">Gross</th>
                                        <th className="px-3 py-2 font-medium text-right">PF change</th>
                                        <th className="px-3 py-2 font-medium text-right">Take-home</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(preview?.result || []).map((row) => (
                                        <tr key={row.employee_id} className="border-t border-gray-200 dark:border-gray-700">
                                            <td className="px-3 py-2">
                                                <span className="font-medium text-gray-900 dark:text-white">
                                                    {row.employee_name || "-"}
                                                </span>
                                                <span className="block text-xs text-muted-foreground">{row.employee_code}</span>
                                            </td>
                                            <td className="px-3 py-2 text-right tabular-nums">{money(row.basic)}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{money(row.current_da)}</td>
                                            <td className="px-3 py-2 text-right tabular-nums font-medium">{money(row.new_da)}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">
                                                {money(row.new_gross)}
                                                {row.gross_change ? (
                                                    <span className="block text-xs text-emerald-600 dark:text-emerald-400">
                                                        +{money(row.gross_change)}
                                                    </span>
                                                ) : null}
                                            </td>
                                            <td className="px-3 py-2 text-right tabular-nums text-amber-700 dark:text-amber-400">
                                                {row.pf_change ? `+${money(row.pf_change)}` : "-"}
                                            </td>
                                            <td className="px-3 py-2 text-right tabular-nums font-medium">
                                                {money(row.take_home_change)}
                                            </td>
                                        </tr>
                                    ))}
                                    {!preview?.result?.length && (
                                        <tr>
                                            <td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                                                No employee in scope has a salary structure to revise.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {preview?.not_listed ? (
                            <p className="text-xs text-muted-foreground">
                                Showing the first {preview.result.length} of {preview.result.length + preview.not_listed}{" "}
                                employees. The totals above cover everyone in scope.
                            </p>
                        ) : null}
                    </div>
                )}
            </DialogModal>

            <ConfirmPopupModal
                open={isApproveConfirmOpen}
                onClose={() => setIsApproveConfirmOpen(false)}
                onConfirm={handleApprove}
                title="Approve this revision?"
                description="Approving records who authorised the rate. Nobody's salary changes until you apply it."
                confirmLabel="Approve"
                type="info"
                loading={loading}
            />

            <ConfirmPopupModal
                open={isApplyConfirmOpen}
                onClose={() => setIsApplyConfirmOpen(false)}
                onConfirm={handleApply}
                title="Apply to every employee in scope?"
                description="This writes a new salary structure effective from the revision date. The current structure is kept and closed the day before, so past payslips stay reproducible. Preview the impact first if you have not."
                confirmLabel="Apply revision"
                type="warning"
                loading={loading}
            />

            <ConfirmPopupModal
                open={isDeleteConfirmOpen}
                onClose={() => setIsDeleteConfirmOpen(false)}
                onConfirm={handleDeleteDraft}
                title="Delete this draft?"
                description="The draft revision will be removed. Approved and applied revisions cannot be deleted."
                confirmLabel="Delete"
                type="danger"
                destructive={true}
                loading={loading}
            />
        </ContainerCard>
    );
};

export default DARevision;
